import { useState } from "react";
import { Phone } from "lucide-react";

/**
 * LATAM emergency directory. Default: Panamá (context: 2026-02 earthquakes).
 *
 * `tel:` links open the device's NATIVE dialer for public emergency services.
 * This does NOT violate the "no voice calls in-app" rule — Qué Onda Chat has
 * zero user-to-user voice calling. Dialing 911/SINAPROC/etc. is a safety
 * primitive we must expose.
 */

const DIRECTORIES = {
  panama: {
    label: "Panamá",
    entries: [
      { id: "pa-sinaproc", label: "SINAPROC", display: "521-1070 / 911", tel: "5211070" },
      { id: "pa-911", label: "Emergencias", display: "911", tel: "911" },
      { id: "pa-cruz", label: "Cruz Roja", display: "317-8900", tel: "3178900" },
      { id: "pa-bomberos", label: "Bomberos", display: "103", tel: "103" },
    ],
  },
  chile: {
    label: "Chile",
    entries: [
      { id: "cl-amb", label: "SENAPRED / Ambulancia", display: "131", tel: "131" },
      { id: "cl-bomb", label: "Bomberos", display: "132", tel: "132" },
      { id: "cl-cara", label: "Carabineros", display: "133", tel: "133" },
    ],
  },
  colombia: {
    label: "Colombia",
    entries: [
      { id: "co-emerg", label: "Emergencias (línea única)", display: "123", tel: "123" },
      { id: "co-cruz", label: "Cruz Roja", display: "132", tel: "132" },
      { id: "co-bomb", label: "Bomberos", display: "119", tel: "119" },
    ],
  },
  mexico: {
    label: "México",
    entries: [
      { id: "mx-911", label: "Emergencias", display: "911", tel: "911" },
      { id: "mx-cruz", label: "Cruz Roja", display: "065", tel: "065" },
      { id: "mx-locatel", label: "Locatel CDMX", display: "55 5658-1111", tel: "5556581111" },
    ],
  },
  general: {
    label: "General LATAM",
    entries: [
      { id: "gen-911", label: "Emergencias", display: "911", tel: "911" },
      { id: "gen-112", label: "Emergencias (alt.)", display: "112", tel: "112" },
    ],
  },
};

const SAFETY_RULES = [
  "Agáchate, Cúbrete, Sujétate (triángulo de la vida).",
  "Aléjate de ventanas, espejos y objetos que puedan caer.",
  "Localiza el punto de encuentro. NO uses elevadores.",
];

export function SOSDirectory() {
  const [country, setCountry] = useState("panama"); // default: Panamá
  const current = DIRECTORIES[country] || DIRECTORIES.general;

  return (
    <div
      className="q-glass border border-white/10 rounded-2xl p-4 sm:p-5"
      data-testid="sos-directory"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-display text-base font-bold tracking-tight text-slate-100">
          Directorio de emergencia
        </h3>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          data-testid="sos-country-select"
          className="q-select text-xs font-mono"
        >
          {Object.entries(DIRECTORIES).map(([key, val]) => (
            <option key={key} value={key}>
              {val.label}
            </option>
          ))}
        </select>
      </div>

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
        {current.entries.map((e) => (
          <li key={e.id}>
            <a
              href={`tel:${e.tel}`}
              data-testid={`sos-tel-${e.id}`}
              className="sos-tel-link flex items-center justify-between gap-3 rounded-xl px-3 py-2 border border-white/10 bg-black/20 hover:bg-white/5 text-slate-100 no-underline"
            >
              <span className="flex items-center gap-2 min-w-0">
                <Phone size={14} className="shrink-0 text-red-400" />
                <span className="text-sm truncate">{e.label}</span>
              </span>
              <span className="text-sm font-mono font-bold tracking-wide text-red-300 shrink-0">
                {e.display}
              </span>
            </a>
          </li>
        ))}
      </ul>

      <div
        className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-3"
        data-testid="sos-safety-rules"
      >
        <p className="text-[11px] font-mono uppercase tracking-widest text-amber-300 mb-2">
          Reglas de seguridad sísmica
        </p>
        <ol className="list-decimal list-inside space-y-1 text-sm text-slate-200">
          {SAFETY_RULES.map((rule, idx) => (
            <li key={idx} className="leading-snug">
              {rule}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
